import React, {useEffect, useRef, useState} from "react";
import styled, {css} from "styled-components";
import {
  CopyCode,
  Memo,
  RowSpan,
  SaveExcelButton,
  Script,
  Site,
} from "../../assets/GlobalStyles";
import ReactDataGrid from '@inovua/reactdatagrid-enterprise';
import '@inovua/reactdatagrid-enterprise/base.css';
import '../../assets/default-light.scss'
import {useAtom, useSetAtom} from "jotai";
import {modalController} from "../../store";
import {ModalBody, ModalFooter, ModalHeader} from "../modal/Modal";
import {VerticalRule} from "../common/Common";
import {ConvertedMediaComponent} from "../Account/ModalComponents";
import {GuideBody, GuideContainer, GuideHeader, Off, On, PreviewSubmit, ScriptSubject, SwitchBox} from "./styles/common";
import {exportCSV} from "../reports/ExportCSV";

export function SwitchComponent(props){
  const {value, cellProps, eventClick, disabled, disabledMessage} = props
  const [select, setSelect] = useState(value)
  const [changing, setChanging] = useState(false)
  const changingRef = useRef(false)
  const [, setModal] = useAtom(modalController)
  const background = !select ? {background: '#ddd'} : {background: '#1E3A8A'};
  const position = select ? {left: ' calc(100% - 4px)', transform: 'translateX(-100%)'} : null
  const enableBlocked = disabled && !select;

  useEffect(() => {
    setSelect(cellProps.data.publishYn === 'Y');
  }, [cellProps.data.publishYn]);

  const handleClick = async (confirm) => {
    if(!confirm){
      setModal({isShow:false});
      return;
    }
    if(changingRef.current) return;
    const nextValue = cellProps.data.publishYn !== 'Y';
    changingRef.current = true;
    setChanging(true);
    const updated = await eventClick(nextValue);
    if(updated){
      cellProps.data.publishYn = nextValue ? 'Y' : 'N';
      setSelect(nextValue);
    }else{
      setSelect(cellProps.data.publishYn === 'Y');
      window.alert('게재 상태 변경에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    }
    changingRef.current = false;
    setChanging(false);
    setModal({isShow:false});
  }
  const showModal = () => {
    setModal({
      isShow: true,
      width: 500,
      blur: true,
      modalComponent: () => {
        return (
            <PublishModal>
              <PublishModalHeader>
                <div>
                  <strong>지면 게재 상태 변경</strong>
                  <span>선택한 지면의 광고 게재 여부를 변경합니다.</span>
                </div>
                <PublishModalClose type="button" onClick={()=>handleClick(false)} aria-label="닫기">×</PublishModalClose>
              </PublishModalHeader>
              <PublishModalBody>
                <PublishChangeBox>
                  <PublishState $active={select}>{select ? "게재 중" : "게재 중지"}</PublishState>
                  <PublishArrow aria-hidden="true">→</PublishArrow>
                  <PublishState $active={!select}>{select ? "게재 중지" : "게재 중"}</PublishState>
                </PublishChangeBox>
                <PublishNotice>
                  {select
                      ? "변경 후 해당 지면에 광고가 노출되지 않습니다."
                      : "변경 후 해당 지면에 광고 게재가 시작됩니다."}
                </PublishNotice>
              </PublishModalBody>
              <PublishModalFooter>
                <PublishCancelButton type="button" onClick={()=>handleClick(false)}>취소</PublishCancelButton>
                <PublishConfirmButton type="button" onClick={()=>handleClick(true)}>변경</PublishConfirmButton>
              </PublishModalFooter>
            </PublishModal>
        )
      }
    })
  }

  return(
    <SwitchBox
      style={background}
      disabled={changing}
      aria-disabled={enableBlocked || changing}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if(changing) return;
        if(enableBlocked) {
          window.alert(disabledMessage || '심사 승인된 지면만 게재할 수 있습니다.');
          return;
        }
        showModal();
      }}
    >
      <label style={position}/>
      {select ? <On>ON</On>:  <Off>OFF</Off>}
    </SwitchBox>
  )
}

const PublishModal = styled.div`
  overflow:hidden;
  border-top:3px solid var(--color-primary);
  border-radius:11px;
  background:var(--color-surface);
`;

const PublishModalHeader = styled.header`
  display:flex;
  min-height:72px;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  padding:0 22px;
  border-bottom:1px solid var(--color-divider);

  strong,
  span {display:block;}
  strong {color:var(--color-text);font-size:var(--font-size-17);font-weight:750;}
  span {margin-top:5px;color:var(--color-text-muted);font-size:var(--font-size-11);}
`;

const PublishModalClose = styled.button`
  border:0;
  background:transparent;
  color:#7d8792;
  font-size:26px;
  line-height:1;
  cursor:pointer;
`;

const PublishModalBody = styled.main`
  padding:24px 22px;
`;

const PublishChangeBox = styled.div`
  display:flex;
  align-items:center;
  justify-content:center;
  gap:14px;
  padding:18px;
  border:1px solid var(--color-primary-border);
  border-radius:9px;
  background:linear-gradient(135deg,#f7faff 0%,var(--color-primary-soft) 100%);
`;

const PublishState = styled.strong`
  display:inline-flex;
  min-width:96px;
  height:34px;
  align-items:center;
  justify-content:center;
  border:1px solid ${({$active})=>$active?'var(--color-primary-border)':'var(--color-border-strong)'};
  border-radius:17px;
  background:${({$active})=>$active?'var(--color-primary-strong)':'var(--color-surface)'};
  color:${({$active})=>$active?'#fff':'var(--color-text-muted)'};
  font-size:var(--font-size-13);
  font-weight:750;
`;

const PublishArrow = styled.span`
  color:var(--color-primary-strong);
  font-size:20px;
  font-weight:700;
`;

const PublishNotice = styled.p`
  margin:14px 0 0;
  color:var(--color-text-muted);
  font-size:var(--font-size-12);
  line-height:1.6;
  text-align:center;
`;

const PublishModalFooter = styled.footer`
  display:flex;
  justify-content:flex-end;
  gap:8px;
  padding:16px 22px;
  border-top:1px solid var(--color-divider);
  background:#fafbfc;
`;

const PublishActionButton = styled.button`
  min-width:84px;
  height:38px;
  padding:0 16px;
  border-radius:7px;
  font-size:var(--font-size-13);
  font-weight:700;
  cursor:pointer;
`;
const PublishCancelButton = styled(PublishActionButton)`
  border:1px solid var(--color-border);
  background:var(--color-surface);
  color:var(--color-text);
  &:hover{border-color:var(--color-primary-border);background:var(--color-primary-soft);}
`;
const PublishConfirmButton = styled(PublishActionButton)`
  border:1px solid var(--color-primary);
  background:var(--color-primary);
  color:#fff;
  &:hover{background:var(--color-primary-dark);}
`;


function ScriptComponent(props){
  const {cellProps} = props
  const[, setModal] = useAtom(modalController)
  const handleClick = () => {
    setModal({
      isShow: true,
      width: 1320,
      modalComponent: () => {
        return (
          <div>
            <ModalHeader title={'지면 스크립트 발급 안내'}/>
            <ModalBody>
              <ScriptSubject>
                <div>지면 등록이 완료되었습니다.<br/>
                  하단 발급된 광고 API를
                  <a
                      href="https://www.notion.so/API-2afbe20d845e8002a124c20d307295da?source=copy_link"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{fontWeight: "bold", fontSize: '17px', marginLeft: '10px', color: 'rgb(30, 58, 138)'}}
                  >
                    광고 송출 가이드
                  </a>를 참고하여 표출할 광고 콘텐츠 영역에 삽입해주세요.
                </div>
                <div>※ 발급된 스크립트 정보는 지면 관리에서 확인 가능합니다.</div>
              </ScriptSubject>
              <GuideContainer>
                <GuideHeader>광고 API(JSON) <Icon icon={'copyCode'} value={'https://dsp.oaple.co.kr/audio/ad?mediaScriptNo='+cellProps.data.inventoryId + '&output=json'}/></GuideHeader>
                <GuideBody>
                  <pre>{'https://dsp.oaple.co.kr/audio/ad?mediaScriptNo='+cellProps.data.inventoryId + '&output=json'}</pre>
                </GuideBody>
                <VerticalRule style={{margin: "20px 0"}}/>
                <GuideHeader>광고 API(XML) <Icon icon={'copyCode'} value={'https://dsp.oaple.co.kr/audio/ad?mediaScriptNo='+cellProps.data.inventoryId + '&output=xml'}/></GuideHeader>
                <GuideBody>
                  <pre>{'https://dsp.oaple.co.kr/audio/ad?mediaScriptNo='+cellProps.data.inventoryId + '&output=xml'}</pre>
                </GuideBody>
              </GuideContainer>
              <VerticalRule style={{margin: "20px 0"}}/>
            </ModalBody>
            <ModalFooter>
              <PreviewSubmit onClick={() => setModal({isShow: false})}>확인</PreviewSubmit>
            </ModalFooter>
          </div>
        )
      }
    })
  }
  return(
    <Script onClick={e => {
      e.stopPropagation()
      handleClick()
    }}/>
  )
}

function MemoComponent(props){
  const {cellProps} = props
  const setModal = useSetAtom(modalController)
  function handleMemoClick(){
    setModal({
      isShow: true,
      width: 500,
      modalComponent:() => {
        return <ConvertedMediaComponent data={cellProps} setModal={setModal}/>
      }
    })
  }
  return(
    <Memo onClick={e => {
      e.stopPropagation()
      handleMemoClick()
    }}/>
  )
}

export function Icon(props) {
  const handleCopyClipBoard = async (text) => {
    if(navigator.clipboard){
      navigator.clipboard
        .writeText(text)
        .then(()=>{alert('클립보드에 복사되었습니다.')})
        .catch(()=>{alert('복사를 다시 시도해 주세요.')});
    } else {
      if (!document.queryCommandSupported("copy")) {
        return alert("복사하기가 지원되지 않는 브라우저입니다.");
      }

      // 흐름 3.
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.position = "fixed";

      // 흐름 4.
      document.body.appendChild(textarea);
      // focus() -> 사파리 브라우저 서포팅
      textarea.focus();
      // select() -> 사용자가 입력한 내용을 영역을 설정할 때 필요
      textarea.select();
      // 흐름 5.
      document.execCommand("copy");
      // 흐름 6.
      document.body.removeChild(textarea);
      alert("클립보드에 복사되었습니다.");
    }
  };
  return(
    <>
      {props.icon === 'script' &&
        <ScriptComponent cellProps={props.cellProps} />
      }
      {props.icon === 'memo' &&
        <MemoComponent cellProps={props.cellProps.data} />
      }
      {props.icon === 'url' &&
        <a href={props.value} target={'_blank'} rel="noopener noreferrer">
          <Site/>
        </a>
      }
      {props.icon === 'copyCode' &&
        <CopyCode onClick={() => handleCopyClipBoard(props.value)}/>
      }
    </>
  )
}

export const i18n = {
  sortAsc: '오름차순',
  sortDesc: '내림차순',
  unsort:'정렬 해제',
  autoSizeToFit:'크기에 맞게 자동 조정',
  autoresizeThisColumn:'열 자동 조정',
  autoresizeAllColumns:'모든 열 자동 조정',
  columns: '항목 설정',
  pageText: '페이지',
  ofText: '/',
  perPageText: '페이지 당',
  showingText: '페이지',
}

const renderSimplePagination = ({
  skip = 0,
  limit = 1,
  totalCount = 0,
  gotoPage,
  gotoPrevPage,
  gotoNextPage,
  hasPrevPage,
  hasNextPage,
}) => {
  const currentPage = totalCount === 0 ? 1 : Math.floor(skip / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(totalCount / limit));

  return (
    <div className="inovua-react-pagination-toolbar simple-pagination" role="navigation" aria-label="테이블 페이지 이동">
      <button type="button" onClick={gotoPrevPage} disabled={!hasPrevPage} aria-label="이전 페이지">
        <span aria-hidden="true">‹</span>
      </button>
      <div className="simple-pagination__pages">
        {Array.from({length: pageCount}, (_, index) => {
          const page = index + 1;
          const isCurrent = page === currentPage;
          return (
            <button
              key={page}
              type="button"
              className={`simple-pagination__page${isCurrent ? " simple-pagination__page--current" : ""}`}
              onClick={() => !isCurrent && gotoPage(page)}
              aria-label={`${page}페이지`}
              aria-current={isCurrent ? "page" : undefined}
            >
              {page}
            </button>
          );
        })}
      </div>
      <button type="button" onClick={gotoNextPage} disabled={!hasNextPage} aria-label="다음 페이지">
        <span aria-hidden="true">›</span>
      </button>
    </div>
  );
};

const StyledDataGrid = styled(ReactDataGrid)`
  ${({$useListStyles}) => $useListStyles && css`
  && .inovua-react-pagination-toolbar {
    display:flex;
    min-height:52px;
    align-items:center;
    justify-content:center;
    gap:8px;
    padding:8px 12px;
    border-top:1px solid var(--color-divider);
    background:#fafbfc;
    color:var(--color-text-muted);
    font-size:var(--font-size-12);
  }

  && .simple-pagination__pages {
    display:flex;
    align-items:center;
    justify-content:center;
    gap:4px;
  }

  && .inovua-react-pagination-toolbar button {
    display:inline-flex;
    min-width:32px;
    height:32px;
    align-items:center;
    justify-content:center;
    padding:0 8px;
    border:1px solid var(--color-border-strong);
    border-radius:6px;
    background:var(--color-surface);
    color:var(--color-text-secondary);
    cursor:pointer;
    transition:border-color .15s ease,background .15s ease,color .15s ease;
  }

  && .inovua-react-pagination-toolbar button:hover:not(:disabled) {
    border-color:var(--color-primary-border);
    background:var(--color-primary-soft);
    color:var(--color-primary-strong);
  }

  && .inovua-react-pagination-toolbar button:disabled {
    background:var(--color-surface-muted);
    color:#b3bbc4;
    cursor:not-allowed;
    opacity:.65;
  }

  && .simple-pagination .simple-pagination__page {
    min-width:32px;
    padding:0 7px;
    border-color:transparent;
    background:transparent;
    color:var(--color-text-secondary);
    font-weight:600;
  }

  && .simple-pagination .simple-pagination__page--current,
  && .simple-pagination .simple-pagination__page--current:hover:not(:disabled) {
    border-color:var(--color-primary-strong);
    background:var(--color-primary-strong);
    color:#fff;
    cursor:default;
  }

  && .simple-pagination > button span {
    font-size:22px;
    line-height:1;
    transform:translateY(-1px);
  }
  `}
`;

export default function Table (props) {
  const {columns, data, groups, fileName, downloadList } = props
  const [gridRef, setGridRef] = useState(null);
  const isReportTable = props.tableVariant === "report";
  const tableColumns = !isReportTable && Array.isArray(columns)
    ? columns.map(column => {
      const preferredWidth = column.width || column.minWidth || 160;
      const responsiveColumn = {...column};
      delete responsiveColumn.width;
      delete responsiveColumn.flex;
      return {
        ...responsiveColumn,
        minWidth: column.minWidth || Math.min(preferredWidth, 120),
        flex: preferredWidth,
      };
    })
    : columns;
  const rowHeight = isReportTable ? (props.rowHeight || 40) : 48;
  const headerHeight = props.headerHeight || 48;
  const pageLimit = props.limit || props.defaultLimit || 30;
  const dataRowCount = Array.isArray(data)
    ? data.length
    : typeof data === "function" && props.pagination ? pageLimit : 0;
  const visibleRowCount = props.pagination
    ? Math.min(dataRowCount, pageLimit)
    : dataRowCount;
  const displayRowCount = Math.max(10, visibleRowCount);
  const paginationHeight = props.pagination && !props.livePagination ? 52 : 0;
  const contentHeight = headerHeight + (displayRowCount * rowHeight) + paginationHeight + 24;
  const requestedMinHeight = Number(props.style?.minHeight) || 0;
  const gridHeight = Math.max(200, requestedMinHeight, contentHeight);
  const gridStyle = isReportTable
    ? {minHeight: 450, ...props.style}
    : {...props.style, height: gridHeight, minHeight: gridHeight};

  useEffect(() => {
    if(gridRef){
      gridRef.current.setColumnSizesToFit()
    }
  },[gridRef])

  useEffect(() => {
    if (props.downloadTrigger > 0 && gridRef) {
      exportCSV(gridRef, fileName);
    }
    // 외부 다운로드 버튼이 실행될 때 현재 테이블 데이터를 저장합니다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.downloadTrigger]);

  const emptyText = <p style={{
    fontSize: 16,
  }}>{props.emptyText !== undefined ? props.emptyText : '데이터가 없습니다.' }</p>
  /**
   * 오른쪽 마우스 클릭 방지
   * @param menuProps
   * @param rowProps
   */
  const renderRowContextMenu = (menuProps, { rowProps }) => {
    menuProps.autoDismiss = true
  }

  const gridElement = (
    <StyledDataGrid
      $useListStyles={!isReportTable}
      licenseKey={process.env.REACT_APP_DATA_GRID_LICENSE_KEY}
      idProperty={props.idProperty}
      handle={setGridRef}
      dataSource={data}
      headerHeight={48}
      showZebraRows={false}
      showCellBorders={'horizontal'}
      groups={groups !== null ? groups : false}
      enableColumnAutosize={true}
      showColumnMenuLockOptions={false}
      showColumnMenuGroupOptions={false}
      emptyText={emptyText}
      limit={pageLimit}
      pagination={props.pagination}
      livePagination={props.livePagination}
      renderPaginationToolbar={isReportTable ? props.renderPaginationToolbar : (props.renderPaginationToolbar || renderSimplePagination)}
      scrollThreshold={props.scrollThreshold}
      showHoverRows={false}
      activeCell={null}
      i18n={i18n}
      renderRowContextMenu={renderRowContextMenu}
      {...props}
      columns={tableColumns}
      rowHeight={rowHeight}
      style={gridStyle}
    />
  )
  return(
    <>
      {/*엑셀다운 기능 임시 닫음*/}
      {downloadList &&
        <RowSpan style={{justifyContent: 'flex-end'}}>
          <SaveExcelButton onClick={()=>exportCSV(gridRef, fileName)}>엑셀 저장</SaveExcelButton>
        </RowSpan>
      }
      {gridElement}
    </>
  )
}
